import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { RetentionPolicyView, RetentionReportRow, RetentionService } from '../../services/integration.service';

/** Retention settings and the past-retention report. Nothing is deleted or purged by this screen. */
const RetentionPanel: React.FC = () => {
    const toast = useToast();
    const [policies, setPolicies] = useState<RetentionPolicyView[]>([]);
    const [report, setReport] = useState<RetentionReportRow[]>([]);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, r] = await Promise.all([RetentionService.list(), RetentionService.report()]);
            setPolicies(p); setReport(r);
        } catch (e) {
            toast.showApiError(e, 'Failed to load retention settings');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const loadDefaults = async () => {
        setBusy(true);
        try {
            const res = await RetentionService.loadDefaults();
            setNotice(res.verifyNotice);
            toast.showSuccess(res.created > 0 ? `Added ${res.created} default policies` : 'Nothing to add; existing policies were left as they are');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Could not load the defaults');
        } finally {
            setBusy(false);
        }
    };

    const save = async (p: RetentionPolicyView, years: number, legalHold: boolean) => {
        try {
            await RetentionService.save({ documentClass: p.documentClass, years, legalHold, note: p.note ?? undefined });
            await load();
        } catch (e) {
            toast.showApiError(e, 'Could not save the policy');
        }
    };

    return (
        <Box data-help-page="data.retention.page">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How long each class of document is kept. A legal hold freezes a class. This version only reports what is past its
                retention period; it never deletes or purges anything.
            </Typography>
            {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}
            <Button data-help="data.retention.action.loadDefaults" variant="outlined" sx={{ mb: 2 }} disabled={busy} onClick={loadDefaults}>Load defaults</Button>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small" data-help-section="data.retention.policies">
                    <TableHead><TableRow><TableCell data-help="data.retention.col.class">Document class</TableCell><TableCell data-help="data.retention.col.years">Keep (years)</TableCell><TableCell data-help="data.retention.col.hold">Legal hold</TableCell><TableCell data-help="data.retention.col.note">Note</TableCell></TableRow></TableHead>
                    <TableBody>
                        {policies.length === 0 && <TableRow><TableCell colSpan={4}>No policies yet. Press "Load defaults".</TableCell></TableRow>}
                        {policies.map(p => (
                            <TableRow key={p.documentClass}>
                                <TableCell>{p.label}</TableCell>
                                <TableCell>
                                    <TextField data-help="data.retention.action.years" size="small" type="number" defaultValue={p.years} inputProps={{ min: 1, max: 100 }} sx={{ width: 100 }}
                                        onBlur={e => { const y = Number(e.target.value); if (y >= 1 && y <= 100 && y !== p.years) save(p, y, p.legalHold); }} />
                                </TableCell>
                                <TableCell><Checkbox data-help="data.retention.action.hold" checked={p.legalHold} onChange={e => save(p, p.years, e.target.checked)} /></TableCell>
                                <TableCell>{p.note}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom>Documents past retention</Typography>
            <TableContainer component={Paper}>
                <Table size="small" data-help-section="data.retention.report">
                    <TableHead><TableRow><TableCell data-help="data.retention.col.rclass">Document class</TableCell><TableCell data-help="data.retention.col.cutoff">Cutoff date</TableCell><TableCell data-help="data.retention.col.past" align="right">Past retention</TableCell><TableCell data-help="data.retention.col.oldest">Oldest</TableCell><TableCell data-help="data.retention.col.status">Status</TableCell></TableRow></TableHead>
                    <TableBody>
                        {report.length === 0 && <TableRow><TableCell colSpan={5}>Nothing to report.</TableCell></TableRow>}
                        {report.map(r => (
                            <TableRow key={r.documentClass}>
                                <TableCell>{r.label}</TableCell>
                                <TableCell>{r.cutoff}</TableCell>
                                <TableCell align="right">{r.pastRetentionCount}</TableCell>
                                <TableCell>{r.oldest ?? ''}</TableCell>
                                <TableCell>{r.held ? <Chip size="small" color="warning" label="Legal hold" /> : null}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default RetentionPanel;
